import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { userIsLoggedInGuard } from '~shared/guards/user-is-logged-in.guard';
import { IMPERSONATION_LANDING_PATH } from '~shared/services/auth/auth-storage.constants';
import { SwitchuserComponent } from './switchuser/switchuser.component';
import { AuthCallbackComponent } from './auth-callback/auth-callback.component';
import { ResetPasswordComponent } from './reset-password/reset-password.component';
import { ResetPasswordTokenComponent } from './reset-password-token/reset-password-token.component';

/**
 * Les routes de PREMIER NIVEAU de l'application, exportées à dessein.
 *
 * `pied-accessibilite.routes.spec.ts` les énumère pour vérifier que le pied
 * d'accessibilité est rendu sur chacune. L'export est ce qui permet au test de
 * lire la configuration RÉELLE plutôt qu'une liste de chemins recopiée à la
 * main — une liste recopiée oublierait la prochaine route publique, c'est-à-dire
 * exactement le défaut que ce test existe pour arrêter.
 */
export const routes: Routes = [
  {
    path: 'login',
    loadChildren: () =>
      import('./login/login.module').then((m) => m.LoginModule),
  },
  /* {
    path: 'design-system',
    loadChildren: () =>
      import('./design-system/design-system.module').then(
        (m) => m.DesignSystemModule
      ),
  }, */
  {
    path: 'reset-password',
    component: ResetPasswordComponent,
  },
  {
    // Titre statique : le dernier segment d'URL est le jeton, qui ne nomme pas
    // l'écran. Chaîne en dur (fr, langue par défaut de ces écrans publics) :
    // un `title` de route est une chaîne, pas une clé i18n.
    path: 'reset-password/:token',
    component: ResetPasswordTokenComponent,
    title: 'Nouveau mot de passe',
  },
  {
    // Titre statique : « switchuser » déduit un titre peu parlant. « Prise
    // d'identité » reprend le vocabulaire du domaine (cf. IMPERSONATION).
    path: IMPERSONATION_LANDING_PATH,
    component: SwitchuserComponent,
    title: "Prise d'identité",
  },
  {
    // Retour du fournisseur d'identité (connexion déléguée) : route publique.
    // Titre statique : « callback » ne nomme pas l'écran pour l'utilisateur.
    path: 'auth/callback',
    component: AuthCallbackComponent,
    title: 'Connexion en cours',
  },
  /*
   * La déclaration d'accessibilité est posée ICI, parmi les routes de premier
   * niveau, et non derrière la garde d'authentification : le référentiel exige
   * qu'elle soit atteignable, et une déclaration qu'il faut un compte pour lire
   * ne remplit pas l'obligation.
   *
   * Ce n'est PAS un second mécanisme d'accès public : c'est celui qui sert déjà
   * `login`, `reset-password` et le retour du fournisseur d'identité. Quand la
   * zone portail (F06-6) sera livrée, elle exposera cette même page dans sa
   * propre navigation.
   */
  {
    path: 'accessibilite',
    loadComponent: () =>
      import('./accessibilite/declaration.component').then(
        (m) => m.DeclarationComponent
      ),
  },
  {
    path: '',
    canActivate: [userIsLoggedInGuard],
    loadChildren: () =>
      import('./pages/pages.module').then((m) => m.PagesModule),
  },
  {
    path: '**',
    redirectTo: '/404',
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      // Requis par unsavedChangesGuard. Avec la valeur par défaut ('replace'),
      // annuler une navigation déclenchée par le bouton "précédent" du
      // navigateur laisse l'URL sur la page précédente alors que la page
      // courante reste affichée. 'computed' recalcule la position dans
      // l'historique et restaure l'URL de la page réellement affichée.
      canceledNavigationResolution: 'computed',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
